import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, Lock, Search, Save, UserCheck, Ban } from 'lucide-react';

const Admin = () => {
  const navigate = useNavigate();
  
  // AUTH STATES
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true); 
  const [accessDenied, setAccessDenied] = useState(false); 

  // ADMIN TOOL STATES
  const [pin, setPin] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [selectedTier, setSelectedTier] = useState('DREAMER');
  const [statusMsg, setStatusMsg] = useState('');

  // 1. THE SAFEGUARD
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthChecking(false);
      
      if (user) {
        setCurrentUser(user);
        const myEmail = 'joshua.morgan@millionorbustapp.com';
        if (user.email.toLowerCase() === myEmail.toLowerCase()) {
             setAccessDenied(false); 
        } else {
             setAccessDenied(true); 
        }
      } else {
        setAccessDenied(true); 
      }
    });
    return () => unsubscribe();
  }, []);

  // 2. PIN CHECK
  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === '2026') setIsUnlocked(true);
    else { alert('INVALID PIN'); setPin(''); }
  };

  // 3. SEARCH & UPDATE LOGIC
  const handleSearch = async (e) => {
    e.preventDefault();
    setStatusMsg('SEARCHING...');
    try {
        const q = query(collection(db, 'users'), where('email', '==', searchEmail));
        const snap = await getDocs(q);
        if (snap.empty) {
            setStatusMsg('USER NOT FOUND.');
        } else {
            const d = snap.docs[0];
            setFoundUser({ id: d.id, ...d.data() });
            setSelectedTier(d.data().tier || 'DREAMER');
            setStatusMsg('USER LOCATED.');
        }
    } catch (err) { 
        // REVEAL THE ENEMY: Show the actual error message
        console.error("FULL ERROR:", err);
        setStatusMsg('ERROR: ' + err.message); 
    }
  };

  const handleUpdate = async () => {
    if (!foundUser) return;
    try {
        await updateDoc(doc(db, 'users', foundUser.id), { tier: selectedTier });
        setStatusMsg('SUCCESS: PROMOTED TO ' + selectedTier);
    } catch (err) { setStatusMsg('UPDATE FAILED: ' + err.message); }
  };

  if (authChecking) {
      return <div style={styles.centerMsg}>INITIALIZING SECURITY PROTOCOLS...</div>;
  }

  if (accessDenied) {
    return (
        <div style={styles.centerMsg}>
            <Ban size={64} color="red" />
            <h1 style={{color: 'red', fontFamily: 'Orbitron', marginTop: '20px'}}>ACCESS DENIED</h1>
            <p style={{color: '#666', marginBottom: '30px'}}>RESTRICTED TO LEAD ARCHITECT ONLY.</p>
            <div style={styles.diagnosticBox}>
                <p><strong>STATUS:</strong> {currentUser ? 'LOGGED IN' : 'LOGGED OUT'}</p>
                <p><strong>DETECTED EMAIL:</strong> {currentUser?.email || 'NULL'}</p>
                <p><strong>REQUIRED EMAIL:</strong> joshua.morgan@millionorbustapp.com</p>
            </div>
            <button onClick={() => navigate('/login')} style={styles.loginBtn}>GO TO LOGIN</button>
        </div>
    );
  }

  if (!isUnlocked) {
    return (
        <div style={styles.centerMsg}>
            <div style={styles.lockBox}>
                <Lock size={48} color="#D4AF37" />
                <h2 style={{color: '#fff', fontFamily: 'Orbitron'}}>SECURITY CLEARANCE</h2>
                <form onSubmit={handlePinSubmit}>
                    <input type="password" value={pin} onChange={e=>setPin(e.target.value)} style={styles.pinInput} maxLength={4} autoFocus />
                    <button type="submit" style={styles.unlockBtn}>ENTER CODE</button>
                </form>
            </div>
        </div>
    );
  }

  return (
    <div style={styles.dashboard}>
        <div style={styles.header}>
            <ShieldAlert size={32} color="#D4AF37" />
            <h1 style={{fontFamily: 'Orbitron', margin: 0}}>GOD MODE // ADMIN</h1>
        </div>

        <div style={styles.panel}>
            <h3>USER MANAGEMENT</h3>
            <form onSubmit={handleSearch} style={{display: 'flex', gap: '10px', marginBottom: '20px'}}>
                <input placeholder="USER EMAIL" value={searchEmail} onChange={e=>setSearchEmail(e.target.value)} style={styles.input} />
                <button type="submit" style={styles.iconBtn}><Search size={20}/></button>
            </form>
            
            {statusMsg && <p style={{color: '#D4AF37', fontFamily: 'Courier New', fontWeight: 'bold'}}>{statusMsg}</p>}

            {foundUser && (
                <div style={styles.editor}>
                    <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'15px'}}>
                        <UserCheck size={20} color="#D4AF37"/>
                        <span>{foundUser.firstName} {foundUser.lastName}</span>
                    </div>
                    <select value={selectedTier} onChange={e=>setSelectedTier(e.target.value)} style={styles.dropdown}>
                        <option value="DREAMER">DREAMER</option>
                        <option value="ARCHITECT">ARCHITECT</option>
                        <option value="TYCOON">TYCOON</option>
                    </select>
                    <button onClick={handleUpdate} style={styles.updateBtn}><Save size={16}/> UPDATE USER</button>
                </div>
            )}
        </div>
    </div>
  );
};

const styles = {
  centerMsg: { height: '100vh', background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'Courier New' },
  dashboard: { minHeight: '100vh', background: '#000', padding: '40px', color: '#fff', fontFamily: 'Inter' },
  header: { display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '40px', borderBottom: '1px solid #333', paddingBottom: '20px', color: '#D4AF37' },
  panel: { background: '#111', padding: '30px', border: '1px solid #333', borderRadius: '10px', maxWidth: '600px' },
  lockBox: { background: '#111', padding: '40px', border: '2px solid #D4AF37', borderRadius: '10px', textAlign: 'center' },
  pinInput: { background: '#000', border: '1px solid #333', color: '#fff', fontSize: '2rem', textAlign: 'center', letterSpacing: '10px', padding: '10px', width: '150px', display: 'block', margin: '20px auto' },
  unlockBtn: { background: '#D4AF37', border: 'none', padding: '15px 30px', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Orbitron', display: 'block', width: '100%' },
  input: { flex: 1, background: '#000', border: '1px solid #333', color: '#fff', padding: '15px', fontFamily: 'Courier New' },
  iconBtn: { background: '#333', border: 'none', color: '#D4AF37', width: '50px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  editor: { background: '#000', padding: '20px', border: '1px solid #333', marginTop: '20px' },
  dropdown: { width: '100%', padding: '15px', background: '#111', color: '#fff', border: '1px solid #D4AF37', marginBottom: '15px', fontFamily: 'Orbitron' },
  updateBtn: { width: '100%', padding: '15px', background: 'green', color: '#fff', border: 'none', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
  loginBtn: { marginTop: '20px', padding: '10px 20px', background: 'transparent', border: '1px solid red', color: 'red', cursor: 'pointer' },
  diagnosticBox: { background: '#111', padding: '20px', border: '1px solid #333', textAlign: 'left', minWidth: '300px' }
};

export default Admin;
